import { Component, inject, DestroyRef, effect, untracked } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, retry, throwError, timeout, timer, TimeoutError } from 'rxjs';
import { AuthService, UserService, AccessRequestService, ServerStatusService, ThemeService, LanguageService, PositionScopeService } from '#services';
import { ServerUnavailableComponent } from '#components';
import { SKIP_ERROR_REDIRECT } from '#core';

/** Includes MSAL's silent token renewal, whose hidden iframe alone may take 10 s after a night's sleep. */
const ME_TIMEOUT_MS = 30_000;
const ME_RETRIES = 2;
const ME_RETRY_DELAY_MS = 2_000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ServerUnavailableComponent],
  // The outlet stays mounted behind the overlay so nothing is torn down (and re-fetched) by a
  // transient outage — the page simply disappears once the API answers again.
  template: `<router-outlet />
    @if (isServerUnavailable()) { <app-server-unavailable /> }`,
  styleUrl: './app.scss',
})
export class App {
  private _authService = inject(AuthService);
  private _userService = inject(UserService);
  private _accessRequestService = inject(AccessRequestService);
  private _languageService = inject(LanguageService);
  private _positionScope = inject(PositionScopeService);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);
  private _rolesFetched = false;

  isServerUnavailable = inject(ServerStatusService).isUnavailable;

  constructor() {
    // Applies the stored (or OS) colour theme before the first paint of any feature page.
    inject(ThemeService).initialize();

    // Resolve the MSAL account first: if the effect below ran before this, it would see
    // "not authenticated", mark roles ready with an empty permission set, and permission
    // guards would bounce the user to /unauthorized.
    this._authService.initialize();

    // Only fetch roles from API once the user is authenticated.
    // Calling getMe() before auth is ready causes MsalInterceptor to trigger
    // a competing loginRedirect which produces the Microsoft "couldn't sign you in" loop.
    effect(() => {
      const authenticated = this._authService.isAuthenticated();
      untracked(() => {
        if (authenticated) {
          if (this._rolesFetched) return;
          this._rolesFetched = true;
          // What the request below is scoped to. If `setPositions` lands somewhere else — no
          // stored choice yet, or a stored one the user no longer holds — the permissions it
          // returned describe a different position and have to be re-read.
          const scopedTo = this._positionScope.selectedId();
          this._userService.getMe({ context: new HttpContext().set(SKIP_ERROR_REDIRECT, true) })
            .pipe(
              timeout(ME_TIMEOUT_MS),
              retry({ count: ME_RETRIES, delay: (err, attempt) => this._retryDelay(err, attempt) }),
              takeUntilDestroyed(this._destroyRef),
            )
            .subscribe({
              next: user => {
                if (user == null) {
                  // 204 No Content — user authenticated but not provisioned
                  this._checkExistingRequest();
                  return;
                }
                // Permissions drive nav/UI visibility; set them before the layout unblocks.
                this._authService.setPermissions(user.Permissions ?? []);
                this._authService.setScopeNodes(user.ScopeNodes ?? []);
                // Restores the stored choice and sends it on every later request as X-Position-Id,
                // which is what narrows those permissions to one position in the first place.
                this._positionScope.setPositions(user.Positions ?? []);
                this._authService.markRolesReady();

                if (this._positionScope.selectedId() !== scopedTo) this._refreshScopedPermissions();

                // The language the user picked on any device, web or mobile. Fetched after /me
                // because an unprovisioned caller has no settings row to read.
                this._languageService.syncFromServer();
              },
              error: (err: HttpErrorResponse) => {
                // Let a later auth event try again — otherwise one failed call leaves the session
                // permanently permission-less and every guarded route bounces to /unauthorized.
                this._rolesFetched = false;
                if (err?.status === 401) {
                  this._checkExistingRequest();
                } else if (err?.status === 403) {
                  // Insufficient permissions
                  this._redirect(['/unauthorized']);
                } else {
                  // Unknown is not "none" (guards read [] as Unauthorized); the kept address lets a reload retry.
                  this._redirect(['/server-error'], { skipLocationChange: true });
                }
              }
            });
        } else {
          // Not authenticated — mark roles ready so the layout renders.
          // MsalGuard will redirect to login if the route requires auth.
          this._authService.markRolesReady();
        }
      });
    });
  }

  /**
   * Re-reads `/me` under the position that was actually selected. The permission set the app holds
   * has to be the one the API will enforce — anything wider enables controls whose requests 403.
   */
  private _refreshScopedPermissions(): void {
    this._userService.getMe({ context: new HttpContext().set(SKIP_ERROR_REDIRECT, true) })
      .pipe(timeout(10000), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: user => {
          if (user == null) return;
          this._authService.setPermissions(user.Permissions ?? []);
          this._authService.setScopeNodes(user.ScopeNodes ?? []);
        },
        // Keep the wider set rather than stranding the user with none; every write is still
        // gated server-side.
        error: () => undefined,
      });
  }

  /** Retries what can pass — no answer in time, no connection, a server failure — never a refusal. */
  private _retryDelay(err: unknown, attempt: number): Observable<number> {
    const status = (err as HttpErrorResponse | null)?.status ?? -1;
    const transient = err instanceof TimeoutError || status === 0 || status >= 500;
    return transient ? timer(attempt * ME_RETRY_DELAY_MS) : throwError(() => err);
  }

  private _checkExistingRequest(): void {
    this._accessRequestService.getMyRequest()
      .pipe(timeout(10000), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: request => {
          if (request == null) {
            // 204 No Content — no existing request, go to registration
            this._redirect(['/register']);
            return;
          }
          if (request.Status === 'Pending' || request.Status === 'Approved') {
            // Approved: the account exists but getMe() still 204'd — most likely the user has no
            // User Group / role assignment yet (a separate admin step from approval), or the BU
            // membership cache hasn't caught up. Show the status page (it already has an
            // "Approved — please refresh" message) rather than bouncing to registration, which
            // would let the user resubmit a duplicate request for an already-approved identity.
            this._redirect(['/access-pending']);
          } else {
            // Rejected/Cancelled — allow re-registration
            this._redirect(['/register']);
          }
        },
        error: () => {
          // Error checking request — go to registration
          this._redirect(['/register']);
        },
      });
  }

  /**
   * The layout renders a full-page spinner until roles are ready, so a redirect that never
   * completes (cancelled guard, failed lazy chunk) would leave the user spinning forever.
   * Release the spinner once the navigation has settled either way.
   */
  private _redirect(...args: Parameters<Router['navigate']>): void {
    this._router.navigate(...args)
      .catch(() => undefined)
      .finally(() => this._authService.markRolesReady());
  }
}
